import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CollegeSelectionProvider } from './context/CollegeSelectionContext';
import { ToastProvider } from './context/ToastContext';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import PricingPage from './pages/PricingPage';
import FeaturesPage from './pages/FeaturesPage';
import HowItWorksPage from './pages/HowItWorksPage';
import LearnMorePage from './pages/LearnMorePage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import Disclaimer from './pages/Disclaimer';
import AboutUs from './pages/AboutUs';
import FAQ from './pages/FAQ';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Contact from './pages/Contact';
import HelpCenter from './pages/HelpCenter';
import HelpArticle from './pages/HelpArticle';
import CollegeDatabase from './pages/CollegeDatabase';
import CutoffTrends from './pages/CutoffTrends';
import FeedbackButton from './components/feedback/FeedbackButton';
import ScrollToTop from './components/common/ScrollToTop';

// Lazy loaded components
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CollegeList = lazy(() => import('./pages/CollegeList'));
const CollegeDetails = lazy(() => import('./pages/CollegeDetails'));
const SelectedColleges = lazy(() => import('./pages/SelectedColleges'));
const ChatbotPage = lazy(() => import('./pages/ChatbotPage'));
const ChatbotComingSoon = lazy(() => import('./pages/ChatbotComingSoon'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const CollegePredictor = lazy(() => import('./pages/CollegePredictor'));
const CutoffAnalyzer = lazy(() => import('./pages/CutoffAnalyzer'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const DeploymentStatus = lazy(() => import('./pages/DeploymentStatus'));
const BlogEditor = lazy(() => import('./pages/BlogEditor'));
const BlogList = lazy(() => import('./pages/BlogList'));
const TestChat = lazy(() => import('./pages/TestChat'));

// Loading component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7B4AE2]"></div>
  </div>
);

function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <ToastProvider>
          <CollegeSelectionProvider>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/learn-more" element={<LearnMorePage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/help/article/:slug" element={<HelpArticle />} />
              <Route path="/college-database" element={<CollegeDatabase />} />
              <Route path="/cutoff-trends" element={<CutoffTrends />} />
              
              <Route element={<Layout />}>
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <Dashboard />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/settings" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <SettingsPage />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <ProfilePage />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/colleges" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <CollegeList />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/colleges/:id" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <CollegeDetails />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/selected-colleges" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <SelectedColleges />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/chat" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <ChatbotPage />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/chatbot-coming-soon" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <ChatbotComingSoon />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/onboarding" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <OnboardingPage />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/college-predictor" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <CollegePredictor />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/cutoff-analyzer/:id" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <CutoffAnalyzer />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <AdminPanel />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/deployment-status" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <DeploymentStatus />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/blog-editor" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <BlogEditor />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/blog-editor/:id" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <BlogEditor />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/blog-list" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <BlogList />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/test-chat" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingFallback />}>
                        <TestChat />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
              </Route>
            </Routes>
            <FeedbackButton />
          </CollegeSelectionProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;